import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: component in src/components/insurance/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ShieldCheck } from 'lucide-react';
import type { ICompanyInsuranceTypesTabProps, TOrgInsuranceTypeItem } from '@/@types/company';

export function CompanyInsuranceTypesTab({
  companyId,
  orgId,
  allowedInsuranceTypeIds,
}: ICompanyInsuranceTypesTabProps) {
  const orgTypes = useQuery(api['insurance/types'].listOrgTypes, { orgId }) as
    | TOrgInsuranceTypeItem[]
    | undefined; // AS_JUSTIFIED: bracket-notation api path loses return type inference; shape matches listOrgTypes validator
  const updateAllowed = useMutation(api.companies.mutations.updateAllowedInsuranceTypes);

  // Track explicit allowed set; undefined means "all enabled" (unset)
  const [explicitIds, setExplicitIds] = useState<Id<'orgInsuranceTypes'>[] | undefined>(
    allowedInsuranceTypeIds
  );

  // Sync when prop changes (e.g. after upstream refetch)
  useEffect(() => {
    setExplicitIds(allowedInsuranceTypeIds);
  }, [allowedInsuranceTypeIds]);

  function isEnabled(typeId: Id<'orgInsuranceTypes'>): boolean {
    if (explicitIds === undefined) return true;
    return explicitIds.includes(typeId);
  }

  async function handleToggle(typeId: Id<'orgInsuranceTypes'>, checked: boolean) {
    if (!orgTypes) return;

    let nextIds: Id<'orgInsuranceTypes'>[];

    if (explicitIds === undefined) {
      // First toggle-off from "all enabled" — switch to explicit mode
      nextIds = checked
        ? orgTypes.map((t: TOrgInsuranceTypeItem) => t._id)
        : orgTypes
            .map((t: TOrgInsuranceTypeItem) => t._id)
            .filter((id: Id<'orgInsuranceTypes'>) => id !== typeId);
    } else {
      nextIds = checked ? [...explicitIds, typeId] : explicitIds.filter(id => id !== typeId);
    }

    setExplicitIds(nextIds);
    await updateAllowed({ companyId, allowedInsuranceTypeIds: nextIds });
  }

  if (orgTypes === undefined) {
    return (
      <div
        className="bg-white rounded-xl border border-border overflow-hidden"
        aria-label="Loading insurance types"
      >
        <div className="px-5 py-4 border-b border-border flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-3 w-72" />
          </div>
        </div>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className={cn(
              'flex items-center justify-between px-5 py-3.5 border-b border-border last:border-0',
              i % 2 === 0 ? 'bg-[#FAFAFA]' : 'bg-white'
            )}
          >
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-5 w-8 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (orgTypes.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-border p-8 text-center">
        <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          No insurance types have been configured for this organisation yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-3">
        <div className="rounded-lg bg-muted p-2 shrink-0">
          <ShieldCheck className="w-4 h-4 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Insurance Types Access</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Select which insurance types employees of this company can apply for
          </p>
        </div>
      </div>

      <ul role="list">
        {orgTypes.map((type: TOrgInsuranceTypeItem, index: number) => {
          const enabled = isEnabled(type._id);
          const rowId = `insurance-type-toggle-${type._id}`;
          return (
            <li
              key={type._id}
              className={cn(
                'flex items-center justify-between px-5 py-3.5 border-b border-border last:border-0',
                index % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white'
              )}
            >
              <label htmlFor={rowId} className="text-sm text-foreground cursor-pointer select-none">
                {type.name}
              </label>
              <Switch
                id={rowId}
                checked={enabled}
                onCheckedChange={checked => void handleToggle(type._id, checked)}
                aria-label={`${enabled ? 'Disable' : 'Enable'} ${type.name}`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
